import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { Eye, EyeSlash } from '@phosphor-icons/react'
import useSessionStore from '../Context/useSessionStore'
import { completeNewPassword, forgotPasswordSubmit, getPendingNewPasswordUser } from '../Hooks/useAuth'
import { InputMessageError } from 'Organisms/InputMessageError'
import { LOGIN_ROUTER } from 'constant/router'
import { COLOR_BLUE_MATE } from 'constant/colors'
import { PasswordRequirement } from './Welcome/Components'
import { Logo } from 'Organisms'

const ResetPassword = () => {
  const [searchParams] = useSearchParams()

  const [errorMessage, setErrorMessage] = useState('')
  const [passwordType, setPasswordType] = useState(true)
  const [confirmPasswordType, setConfirmPasswordType] = useState(true)

  const { challenge, updateChallenge } = useSessionStore(
    (state) => ({
      user: state.user,
      updateUser: state.updateUser,
      challenge: state.challenge,
      updateChallenge: state.updateChallenge
    })
  )

  const { register, handleSubmit, formState: { errors } } = useForm()

  const navigate = useNavigate()

  const onSubmit = (data) => {
    setErrorMessage('')

    if (data.password !== data.passwordConfirm) {
      setErrorMessage('Passwords must match')
      return
    }

    const userName = searchParams.get('user_name')
    const confirmationCode = searchParams.get('confirmation_code')
    const challengeUser = getPendingNewPasswordUser()

    // Two ways to land here:
    //  - temp-password / first login: Login.js kept the live Cognito
    //    NEW_PASSWORD_REQUIRED user -> finish it with completeNewPassword
    //  - forgot-password email deep link: ?user_name=&confirmation_code= present
    const request = challengeUser
      ? completeNewPassword(challengeUser, data.password, challenge?.required_attributes)
      : userName && confirmationCode
        ? forgotPasswordSubmit(userName.toLowerCase().replace(' ', '+'), confirmationCode, data.password)
        : null

    if (!request) {
      setErrorMessage('This reset link is invalid or has expired. Please sign in again.')
      return
    }

    request
      .then(() => {
        updateChallenge(null)
        navigate(LOGIN_ROUTER)
      })
      .catch((error) => {
        console.error(error)
        setErrorMessage(error?.message || 'Could not set the new password')
      })
  }

  return (
    <div className="container login dark-theme">
      <div className="row justify-content-center align-items-center">
        <div className="col-12 col-md-8 col-lg-5">
          <div className="row justify-content-center align-items-center">
            <div className="col-12 col-md-8">
              <div className="row justify-content-center">
                <div className="col-9 logo-wrapper">
                  <Logo />
                </div>
              </div>
              <div className="row justify-content-center mt-5">
                  <h2>Set new password</h2>
                  <p>Must be at least 8 characters.</p>
                <form className="col-12 d-grid" onSubmit={handleSubmit(onSubmit)}>
                  {errorMessage && <InputMessageError error={errorMessage} />}
                  {errors.password && <InputMessageError error='Password must have at least 8 characters, 1 uppercase letter, 1 lowercase letter, 1 number, and 1 special character' />}
                  <div className="mb-3 position-relative">
                    <label className="form-label d-flex align-items-center" htmlFor="inputPassword">
                      Password
                      <PasswordRequirement />
                    </label>
                    <input
                      type={passwordType ? 'password' : 'text'}
                      {...register('password', {
                        required: true,
                        pattern: /^(?=\S*[a-z])(?=\S*[A-Z])(?=\S*\d)(?=\S*([^\w\s]|[_]))\S{8,}$/
                      })}
                      className={`form-control ${errorMessage ? 'is-invalid' : ''}`}
                      id="inputPassword"
                      aria-describedby="emailHelp"
                      placeholder="New password"
                    />
                    <button
                      type="button"
                      className="btn btn-ghost position-absolute eye-button"
                      onClick={() => setPasswordType(!passwordType)}>
                      {!passwordType && <Eye size={18} color={COLOR_BLUE_MATE} weight="regular" />}
                      {passwordType && <EyeSlash size={18} color={COLOR_BLUE_MATE} weight="regular" />}
                    </button>
                  </div>
                  <div className="mb-3 position-relative">
                    <label className="form-label" htmlFor="inputConfirmPassword">
                      Confirm password
                    </label>
                    <input
                      type={confirmPasswordType ? 'password' : 'text'}
                      {...register('passwordConfirm', { required: true })}
                      className="form-control"
                      id="inputConfirmPassword"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      className="btn btn-ghost position-absolute eye-button"
                      onClick={() => setConfirmPasswordType(!confirmPasswordType)}>
                      {!confirmPasswordType && <Eye size={18} color={COLOR_BLUE_MATE} weight="regular" />}
                      {confirmPasswordType && <EyeSlash size={18} color={COLOR_BLUE_MATE} weight="regular" />}
                    </button>
                  </div>
                  <button type="submit" className="btn btn-block btn-primary">
                    Set new password
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
